import { clampListQuery } from '@lib/pagination';
import { http } from './http';
import type {
  ApiSuccess,
  Camera,
  CameraListQuery,
  CameraType,
  CameraStatus,
  ManualRecordingStatus,
  RecordingMode,
  StorageTarget,
  PaginatedResult,
  RecordingSchedulePutBody,
  RecordingScheduleResponse,
} from './types';

const PATH = '/api/cameras';

export interface CameraCreateInput {
  name: string;
  rtspUrl: string;
  groupId?: number | null;
  type?: CameraType;
  recordingMode?: RecordingMode;
  recordingEnabled?: boolean;
  storageTarget?: StorageTarget;
  status?: CameraStatus;
  isFavorite?: boolean;
}

export type CameraUpdateInput = Partial<CameraCreateInput>;

export const camerasApi = {
  async list(
    query: CameraListQuery = {},
    signal?: AbortSignal,
  ): Promise<PaginatedResult<Camera>> {
    const res = await http.get<ApiSuccess<PaginatedResult<Camera>>>(PATH, {
      params: stripUndefined(clampListQuery(query)),
      signal,
    });
    return res.data.data;
  },

  async get(id: number, signal?: AbortSignal): Promise<Camera> {
    const res = await http.get<ApiSuccess<Camera>>(`${PATH}/${id}`, { signal });
    return res.data.data;
  },

  async create(input: CameraCreateInput): Promise<Camera> {
    const res = await http.post<ApiSuccess<Camera>>(PATH, stripUndefinedKeys(input));
    return res.data.data;
  },

  async update(id: number, input: CameraUpdateInput): Promise<Camera> {
    const res = await http.put<ApiSuccess<Camera>>(`${PATH}/${id}`, stripUndefinedKeys(input));
    return res.data.data;
  },

  async remove(id: number): Promise<void> {
    await http.delete(`${PATH}/${id}`);
  },

  async setStatus(id: number, status: 'active' | 'inactive'): Promise<Camera> {
    const res = await http.patch<ApiSuccess<Camera>>(`${PATH}/${id}/status`, { status });
    return res.data.data;
  },

  async setFavorite(id: number, isFavorite: boolean): Promise<Camera> {
    const res = await http.patch<ApiSuccess<Camera>>(`${PATH}/${id}/favorite`, { isFavorite });
    return res.data.data;
  },

  async getRecordingSchedule(id: number, signal?: AbortSignal): Promise<RecordingScheduleResponse> {
    const res = await http.get<ApiSuccess<RecordingScheduleResponse>>(
      `${PATH}/${id}/recording-schedule`,
      { signal },
    );
    return res.data.data;
  },

  async putRecordingSchedule(
    id: number,
    body: RecordingSchedulePutBody,
    signal?: AbortSignal,
  ): Promise<RecordingScheduleResponse> {
    const res = await http.put<ApiSuccess<RecordingScheduleResponse>>(
      `${PATH}/${id}/recording-schedule`,
      body,
      { signal },
    );
    return res.data.data;
  },

  async getManualRecording(id: number, signal?: AbortSignal): Promise<ManualRecordingStatus> {
    const res = await http.get<ApiSuccess<ManualRecordingStatus>>(
      `${PATH}/${id}/recording/manual`,
      { signal },
    );
    return res.data.data;
  },

  async startManualRecording(id: number, signal?: AbortSignal): Promise<ManualRecordingStatus> {
    const res = await http.post<ApiSuccess<ManualRecordingStatus>>(
      `${PATH}/${id}/recording/manual/start`,
      undefined,
      { signal },
    );
    return res.data.data;
  },

  async stopManualRecording(id: number, signal?: AbortSignal): Promise<ManualRecordingStatus> {
    const res = await http.post<ApiSuccess<ManualRecordingStatus>>(
      `${PATH}/${id}/recording/manual/stop`,
      undefined,
      { signal },
    );
    return res.data.data;
  },
};

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== '' && v !== null) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

function stripUndefinedKeys<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}
